import React, { useState } from 'react';
import AttachmentUpload from './AttachmentUpload';
import CommonOptionFields from './CommonOptionFields';

const ActivityForm = ({ onSave, onCancel, initialData }) => {
  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    componentType: 'Activity',
    activityType: initialData?.activityType || '',
    description: initialData?.description || '',
    activityName: initialData?.activityName || '',
    address: initialData?.address || '',
    details: initialData?.details || '',
    date: initialData?.date || '',
    time: initialData?.time || '',
    duration: initialData?.duration || '',
    adults: initialData?.adults || 1,
    children: initialData?.children || 0,
    childrenAges: initialData?.childrenAges || [],
    confirmationNumber: initialData?.confirmationNumber || '',
    phoneNumber: initialData?.phoneNumber || '',
    price: initialData?.price || '',
    isOption: initialData?.isOption || false,
    optionDescription: initialData?.optionDescription || '',
    paymentTiming: initialData?.paymentTiming || 'with_deposit',
    optionalDepositAmount: initialData?.optionalDepositAmount || '',
    agentNotes: initialData?.agentNotes || '',
    agentCommission: initialData?.agentCommission ?? null,
    photos: initialData?.photos || [],
    documents: initialData?.documents || []
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.activityType.trim()) {
      newErrors.activityType = 'Activity type is required';
    }
    
    if (!formData.adults || formData.adults < 1) {
      newErrors.adults = 'At least 1 adult is required';
    }
    
    if (formData.children > 0 && formData.childrenAges.length !== parseInt(formData.children)) {
      newErrors.childrenAges = 'Please provide ages for all children';
    }
    
    // Validate children ages
    if (formData.childrenAges.some(age => age === '' || age < 0 || age > 17)) {
      newErrors.childrenAges = 'Children ages must be between 0-17';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    
    onSave({
      ...formData,
      type: 'activity',
      price: parseFloat(formData.price) || 0,
      adults: parseInt(formData.adults),
      children: parseInt(formData.children),
      childrenAges: formData.childrenAges.map(age => parseInt(age)),
      paymentTiming: formData.paymentTiming,
      optionalDepositAmount: formData.optionalDepositAmount ? parseFloat(formData.optionalDepositAmount) : null
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Handle children count change
    if (name === 'children') {
      const childrenCount = parseInt(value) || 0;
      const newChildrenAges = Array(childrenCount).fill('').map((_, index) => 
        formData.childrenAges[index] || ''
      );
      setFormData(prev => ({
        ...prev,
        children: childrenCount,
        childrenAges: newChildrenAges
      }));
    }
  };

  const handleChildAgeChange = (index, age) => {
    const newAges = [...formData.childrenAges];
    newAges[index] = age;
    setFormData(prev => ({
      ...prev,
      childrenAges: newAges
    }));
    
    if (errors.childrenAges) {
      setErrors(prev => ({
        ...prev,
        childrenAges: ''
      }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="component-form activity-form">
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="title">Component Title *</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            placeholder="e.g., Snorkeling Adventure"
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="componentType">Component Type</label>
          <input
            type="text"
            id="componentType"
            name="componentType"
            value={formData.componentType}
            className="readonly-input"
            disabled
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="activityType">Activity Type *</label>
          <input
            type="text"
            id="activityType"
            name="activityType"
            value={formData.activityType}
            onChange={handleInputChange}
            placeholder="e.g., Water Sports, Sightseeing, Adventure"
            className={errors.activityType ? 'error' : ''}
            required
          />
          {errors.activityType && <div className="error-message">{errors.activityType}</div>}
        </div>
        <div className="form-group">
          <label htmlFor="description">Description</label>
          <input
            type="text"
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Brief description of the activity"
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="activityName">Activity/Location Name</label>
          <input
            type="text"
            id="activityName"
            name="activityName"
            value={formData.activityName}
            onChange={handleInputChange}
            placeholder="e.g., Blue Lagoon Tours"
          />
        </div>
        <div className="form-group">
          <label htmlFor="address">Address</label>
          <input
            type="text"
            id="address"
            name="address"
            value={formData.address}
            onChange={handleInputChange}
            placeholder="Activity location address"
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="details">Activity Details</label>
        <textarea
          id="details"
          name="details"
          value={formData.details}
          onChange={handleInputChange}
          placeholder="Detailed information about the activity, what's included, requirements, etc."
          rows="4"
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="date">Date</label>
          <input
            type="date"
            id="date"
            name="date"
            value={formData.date}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-group">
          <label htmlFor="time">Time</label>
          <input
            type="time"
            id="time"
            name="time"
            value={formData.time}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-group">
          <label htmlFor="duration">Duration</label>
          <input
            type="text"
            id="duration"
            name="duration"
            value={formData.duration}
            onChange={handleInputChange}
            placeholder="e.g., 3 hours, Half day"
          />
        </div>
      </div>

      <div className="form-section">
        <h4>Guests *</h4>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="adults">Adults *</label>
            <select
              id="adults"
              name="adults"
              value={formData.adults}
              onChange={handleInputChange}
              className={errors.adults ? 'error' : ''}
              required
            >
              {Array.from({ length: 20 }, (_, i) => (
                <option key={i + 1} value={i + 1}>{i + 1}</option>
              ))}
            </select>
            {errors.adults && <div className="error-message">{errors.adults}</div>}
          </div>
          <div className="form-group">
            <label htmlFor="children">Children</label>
            <select
              id="children"
              name="children"
              value={formData.children}
              onChange={handleInputChange}
            >
              {Array.from({ length: 21 }, (_, i) => (
                <option key={i} value={i}>{i}</option>
              ))}
            </select>
          </div>
        </div>

        {formData.children > 0 && (
          <div className="form-group">
            <label>Children Ages *</label>
            <div className="children-ages-grid">
              {Array.from({ length: parseInt(formData.children) }, (_, index) => (
                <div key={index} className="child-age-input">
                  <label htmlFor={`childAge${index}`}>Child {index + 1}</label>
                  <select
                    id={`childAge${index}`}
                    value={formData.childrenAges[index] || ''}
                    onChange={(e) => handleChildAgeChange(index, e.target.value)}
                    className={errors.childrenAges ? 'error' : ''}
                    required
                  >
                    <option value="">Age</option>
                    {Array.from({ length: 18 }, (_, i) => (
                      <option key={i} value={i}>{i}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            {errors.childrenAges && <div className="error-message">{errors.childrenAges}</div>}
          </div>
        )}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="confirmationNumber">Confirmation Number</label>
          <input
            type="text"
            id="confirmationNumber"
            name="confirmationNumber"
            value={formData.confirmationNumber}
            onChange={handleInputChange}
            placeholder="Booking confirmation number"
          />
        </div>
        <div className="form-group">
          <label htmlFor="phoneNumber">Phone Number</label>
          <input
            type="tel"
            id="phoneNumber"
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleInputChange}
            placeholder="Contact phone number"
          />
        </div>
        <div className="form-group">
          <label htmlFor="price">Price</label>
          <input
            type="number"
            id="price"
            name="price"
            value={formData.price}
            onChange={handleInputChange}
            placeholder="0.00"
            step="0.01"
            min="0"
          />
        </div>
      </div>

      <CommonOptionFields formData={formData} setFormData={setFormData} />

      <AttachmentUpload
        photos={formData.photos}
        documents={formData.documents}
        onPhotosChange={(photos) => setFormData({...formData, photos})}
        onDocumentsChange={(documents) => setFormData({...formData, documents})}
        maxPhotos={5}
        maxDocuments={3}
        showPhotos={true}
        showDocuments={true}
      />

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          Save Activity
        </button>
      </div>
    </form>
  );
};

export default ActivityForm;
